import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Dropdown, Icon, Input, Message, Modal, Pagination } from 'semantic-ui-react';
import styled from 'styled-components';

import DashboardCard from 'areas/dashboard/components/DashboardCard';
import LoadingSpinner from 'common/components/LoadingSpinner';
import PositionedModal from 'common/components/PositionedModal';
import StatusLabel from 'common/components/StatusLabel';
import ToggleControl from 'common/components/ToggleChartControl';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { ControlContainer, ControlContainerRow, PageContent, PageHeader, PageSection } from 'common/styles/page';
import {
  getCurrentOrganization,
  getCurrentOrganizationTimezone,
  isCurrentUserStaffOrSuperAdmin,
} from 'common/selectors/auth';
import { useToasts } from 'common/toast';

import {
  AssessmentQuestionSetOption,
  PulseParticipant,
  useFinalizePulseSurveyMutation,
  useGetPulseParticipantsQuery,
  useListPulseSurveyStructuresQuery,
  useListPulseQuestionSetsQuery,
  useGetPulseSurveyQuery,
  useInvitePulseParticipantsMutation,
  useLazyGetPulseSurveyPreviewLinkQuery,
  useRemindPulseParticipantMutation,
  useRemindPulseParticipantsMutation,
  useRemovePulseParticipantMutation,
  useUpdatePulseSurveyMutation,
} from '../api/pulseSurveyPortalApi';
import { EmailHistoryModal } from 'areas/assessment-schedule/components/AssessmentSurveyDetailsModals';
import PulseSurveyLikertAnalytics from './PulseSurveyLikertAnalytics';
import PulseSurveyFormModal from './PulseSurveyFormModal';
import PulseSurveySummaryCard from './PulseSurveySummaryCard';

const PulseSurveySummaryCardAny = PulseSurveySummaryCard as any;

const errorDetail = (err: any, fallback: string): string =>
  String(err?.data?.detail || err?.data?.error || fallback);

// Same format as the assessment schedule page.
const formatDate = (date: string | Date): string =>
  new Date(date).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

const formatInviteSentDate = (date: string | Date): string =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

const formatHeaderDate = (date: string | Date): string =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const PARTICIPANTS_PAGE_SIZE = 9;
const PULSE_STATUS_IN_PROGRESS = 2;
const ALL_TEAMS_FILTER_VALUE = '__all_teams__';

type PulseSurveyTab = 'schedule' | 'analytics';

const INVITE_STATE_LABEL: Record<PulseParticipant['invite_state'], { label: string; color: string }> = {
  pending: { label: 'Scheduled', color: '#9A9A9A' },
  sent: { label: 'Invited', color: '#1976d2' },
  completed: { label: 'Completed', color: '#2e7d32' },
  failed: { label: 'Email Not Delivered', color: '#d32f2f' },
  canceled: { label: 'Canceled', color: '#424242' },
};

const InviteStateLabel: React.FC<{ participant: PulseParticipant }> = ({ participant }) => {
  const { label, color } = INVITE_STATE_LABEL[participant.invite_state];
  const title = participant.invite_state === 'failed' ? participant.delivery_status || undefined : undefined;
  return (
    <StatusLabel color={color} title={title}>
      {label}
    </StatusLabel>
  );
};

const PulseSurveyDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const surveyId = Number(id);
  const validId = Number.isFinite(surveyId);
  const { addToast } = useToasts();
  const organization = useSelector(getCurrentOrganization) as any;
  const orgTimezone = useSelector(getCurrentOrganizationTimezone) as string | undefined;
  const isStaffOrSuperAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);

  const {
    data: survey,
    isLoading: isSurveyLoading,
    error: surveyError,
    refetch: refetchSurvey,
  } = useGetPulseSurveyQuery(surveyId, {
    skip: !validId,
  });
  const {
    data: participantsData,
    isLoading: isParticipantsLoading,
    error: participantsError,
  } = useGetPulseParticipantsQuery(surveyId, { skip: !validId });

  const finalized = Boolean(participantsData?.participation.finalized);
  const { data: questionSets = [] } = useListPulseQuestionSetsQuery();
  const { data: structures = [] } = useListPulseSurveyStructuresQuery();

  const [invite, { isLoading: isInviting }] = useInvitePulseParticipantsMutation();
  const [removeParticipant] = useRemovePulseParticipantMutation();
  const [remind] = useRemindPulseParticipantsMutation();
  const [remindParticipant] = useRemindPulseParticipantMutation();
  const [finalize, { isLoading: isFinalizing }] = useFinalizePulseSurveyMutation();
  const [updatePulseSurvey, { isLoading: isUpdatingSurvey }] = useUpdatePulseSurveyMutation();
  const [getPreviewLink] = useLazyGetPulseSurveyPreviewLinkQuery();

  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [isTogglingReminderEnabled, setIsTogglingReminderEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState<PulseSurveyTab>('schedule');
  const [selectedAnalyticsParticipantTeamId, setSelectedAnalyticsParticipantTeamId] = useState<number | null>(null);
  const [analyticsParticipantTeamOptions, setAnalyticsParticipantTeamOptions] = useState<
    Array<{ key: string; value: string; text: string; content: React.ReactNode; disabled?: boolean }>
  >([
    {
      key: 'all',
      value: 'all',
      text: 'All teams',
      content: 'All teams',
    },
  ]);
  const initializedTabSurveyIdRef = useRef<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [nameFilter, setNameFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>(ALL_TEAMS_FILTER_VALUE);
  const [historyModal, setHistoryModal] = useState<{
    open: boolean;
    name: string;
    email: string;
    items: {
      email: string;
      sent_date: string;
      status: string;
      error_message?: string | null;
    }[];
  }>({
    open: false,
    name: '',
    email: '',
    items: [],
  });

  const participants = useMemo(
    () =>
      [...(participantsData?.participants ?? [])].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'en', {
          sensitivity: 'base',
        })
      ),
    [participantsData]
  );
  const participation = participantsData?.participation;
  const failedInvites = participants.filter((p) => p.invite_state === 'failed').length;
  const teamFilterOptions = useMemo(() => {
    const teamNames = Array.from(new Set(participants.map((p) => (p.team_name || '').trim()).filter(Boolean))).sort(
      (a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })
    );
    return [
      { key: ALL_TEAMS_FILTER_VALUE, text: 'All teams', value: ALL_TEAMS_FILTER_VALUE },
      ...teamNames.map((teamName) => ({ key: teamName, text: teamName, value: teamName })),
    ];
  }, [participants]);
  const filteredParticipants = useMemo(() => {
    const normalizedNameFilter = nameFilter.trim().toLowerCase();
    return participants.filter((participant) => {
      const matchesName = !normalizedNameFilter || (participant.name || '').toLowerCase().includes(normalizedNameFilter);
      const matchesTeam =
        teamFilter === ALL_TEAMS_FILTER_VALUE || (participant.team_name || '').trim() === teamFilter;
      return matchesName && matchesTeam;
    });
  }, [participants, nameFilter, teamFilter]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredParticipants.length / PARTICIPANTS_PAGE_SIZE)),
    [filteredParticipants.length]
  );
  const paginatedParticipants = useMemo(() => {
    const startIndex = (currentPage - 1) * PARTICIPANTS_PAGE_SIZE;
    return filteredParticipants.slice(startIndex, startIndex + PARTICIPANTS_PAGE_SIZE);
  }, [filteredParticipants, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [surveyId, participants.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [nameFilter, teamFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const defaultTabForSurvey = useMemo<PulseSurveyTab>(() => {
    if (!survey?.end_date_time) {
      return 'schedule';
    }
    const closeAt = new Date(survey.end_date_time).getTime();
    const closedByDate = Number.isFinite(closeAt) && Date.now() > closeAt;
    return closedByDate ? 'analytics' : 'schedule';
  }, [survey?.end_date_time]);

  const effectiveActiveTab: PulseSurveyTab =
    survey?.id && initializedTabSurveyIdRef.current !== survey.id ? defaultTabForSurvey : activeTab;

  // For a survey that's already past its close date, default directly to analytics.
  // This runs once per survey id and still allows manual tab changes afterwards.
  useEffect(() => {
    if (!survey?.id || !survey?.end_date_time) {
      return;
    }
    if (initializedTabSurveyIdRef.current === survey.id) {
      return;
    }

    setActiveTab(defaultTabForSurvey);
    initializedTabSurveyIdRef.current = survey.id;
  }, [survey?.id, survey?.end_date_time, defaultTabForSurvey]);

  const isInProgress = survey?.status === PULSE_STATUS_IN_PROGRESS;

  // Opens in a new tab. The tab is opened inside the click (so popup blockers
  // allow it) and pointed at a freshly signed preview link once it arrives.
  const onPreview = async () => {
    const tab = window.open('', '_blank');
    try {
      const { url } = await getPreviewLink(surveyId).unwrap();
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      tab?.close();
      addToast(errorDetail(err, 'Couldn’t open the preview.'), { appearance: 'error', autoDismiss: true });
    }
  };

  const onUninvite = async (participant: PulseParticipant) => {
    setBusyId(participant.id);
    try {
      const result = await removeParticipant({ id: surveyId, participantId: participant.id }).unwrap();
      const message =
        result?.invite_state === 'canceled'
          ? `Canceled member survey for ${participant.name}. Their survey link no longer works.`
          : `Removed ${participant.name}.`;
      addToast(message, { appearance: 'success', autoDismiss: true });
    } catch (err) {
      addToast(errorDetail(err, 'Failed to cancel member survey.'), { appearance: 'error', autoDismiss: true });
    } finally {
      setBusyId(null);
    }
  };

  // Re-inviting a failed or canceled person resets them and sends a fresh invite.
  const onReinvite = async (participant: PulseParticipant) => {
    if (participant.employee_id === null) return;
    setBusyId(participant.id);
    try {
      await invite({ id: surveyId, body: { employee_ids: [participant.employee_id] } }).unwrap();
      addToast(`Invite queued for ${participant.name}.`, { appearance: 'success', autoDismiss: true });
    } catch (err) {
      addToast(errorDetail(err, 'Failed to send the invite.'), { appearance: 'error', autoDismiss: true });
    } finally {
      setBusyId(null);
    }
  };

  const onRemind = async () => {
    try {
      await remind({ id: surveyId }).unwrap();
      addToast('Reminder queued for everyone who hasn’t responded yet.', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (err) {
      addToast(errorDetail(err, 'Failed to send reminder.'), { appearance: 'error', autoDismiss: true });
    }
  };

  const onRemindParticipant = async (participant: PulseParticipant) => {
    setBusyId(participant.id);
    try {
      await remindParticipant({ id: surveyId, participantId: participant.id }).unwrap();
      addToast(`Reminder queued for ${participant.name}.`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (err) {
      addToast(errorDetail(err, `Failed to send reminder to ${participant.name}.`), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setBusyId(null);
    }
  };

  const onViewEmailHistory = (participant: PulseParticipant) => {
    setHistoryModal({
      open: true,
      name: participant.name,
      email: participant.email ?? '',
      items: (participant.email_history ?? []).filter(
        (item): item is { email: string; sent_date: string; status: string; error_message?: string | null } =>
          Boolean(item?.sent_date)
      ),
    });
  };

  const onFinalize = async () => {
    setFinalizeError(null);
    try {
      await finalize({ id: surveyId }).unwrap();
      setConfirmFinalize(false);
      addToast('Pulse survey finalized.', { appearance: 'success', autoDismiss: true });
    } catch (err) {
      setFinalizeError(errorDetail(err, 'Failed to finalize pulse survey.'));
    }
  };

  const onSaveSurvey = async ({ payload }: { payload: any }) => {
    await updatePulseSurvey({ id: surveyId, payload }).unwrap();
    await refetchSurvey();
    addToast('Pulse survey updated.', { appearance: 'success', autoDismiss: true });
  };

  const onToggleReminderEnabled = async () => {
    if (!survey) return;

    setIsTogglingReminderEnabled(true);
    try {
      const enabled = !survey.reminder_enabled;
      await updatePulseSurvey({ id: surveyId, payload: { reminder_enabled: enabled } }).unwrap();
      await refetchSurvey();
      addToast(`Reminders ${enabled ? 'enabled' : 'disabled'}.`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (err) {
      addToast(errorDetail(err, 'Failed to update reminder status.'), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsTogglingReminderEnabled(false);
    }
  };

  if (isSurveyLoading || isParticipantsLoading) return <LoadingSpinner />;

  const structureName =
    survey?.assessment_schedule_structure && structures.length > 0
      ? structures.find((item) => item.id === survey.assessment_schedule_structure)?.name
      : undefined;

  const surveyHeaderTitle = survey
    ? `${(structureName || 'Pulse Survey')} - ${formatHeaderDate(survey.start_date_time)}`
    : '';

  const header = (
    <PageHeader
      title={surveyHeaderTitle}
      data-testid="pulse-survey-header"
      backText="Return to Pulse Surveys"
      onBack={() => navigate('/app/pulse-surveys')}
    />
  );

  if (surveyError || participantsError || !survey || !participantsData || !participation) {
    return (
      <PageContent data-testid="pulse-survey-details-page">
        {header}
        <Message negative content="Failed to load this pulse survey." />
      </PageContent>
    );
  }

  const surveyActionsControl = (
    <SummaryActionsControl>
      <Dropdown
        button
        icon="ellipsis vertical"
        floating
        className="icon summary-actions-menu"
        aria-label="Survey actions"
        direction="left"
      >
        <Dropdown.Menu>
          <Dropdown.Item icon="external alternate" text="Preview Survey" onClick={onPreview} />
          {!finalized && <Dropdown.Item icon="write" text="Edit Properties" onClick={() => setIsEditOpen(true)} />}
          {!finalized && (
            <>
              <Dropdown.Item
                icon="send"
                text="Send Reminders"
                disabled={!isInProgress}
                title={isInProgress ? undefined : 'Reminders can only be sent while the survey is in progress.'}
                onClick={onRemind}
              />
              <Dropdown.Item
                icon="lock"
                text="Finalize"
                disabled={!participantsData.can_finalize}
                onClick={() => {
                  setFinalizeError(null);
                  setConfirmFinalize(true);
                }}
              />
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </SummaryActionsControl>
  );

  const onTabChange = (value: string) => {
    if (value === 'schedule' || value === 'analytics') {
      setActiveTab(value);
    }
  };

  const tabControls = (
    <ControlContainerRow>
      <ControlContainer justify="left">
        {effectiveActiveTab === 'analytics' && (
          <HeaderTeamFilterWrap>
            <Dropdown
              selection
              compact
              options={analyticsParticipantTeamOptions as any}
              value={selectedAnalyticsParticipantTeamId === null ? 'all' : String(selectedAnalyticsParticipantTeamId)}
              onChange={(_, dropdownData) => {
                const value = String(dropdownData.value ?? 'all');
                setSelectedAnalyticsParticipantTeamId(value === 'all' ? null : Number(value));
              }}
              aria-label="Analytics team filter"
            />
          </HeaderTeamFilterWrap>
        )}
        <TabToggleWrap>
          <ToggleControl
            buttonLabels={[
              { type: 'schedule', icon: 'calendar alternate outline' },
              { type: 'analytics', icon: 'chart line' },
            ]}
            activeToggle={effectiveActiveTab}
            setChartType={onTabChange}
          />
        </TabToggleWrap>
      </ControlContainer>
    </ControlContainerRow>
  );

  return (
    <PageContent data-testid="pulse-survey-details-page">
      <PageHeader
        title={surveyHeaderTitle}
        data-testid="pulse-survey-header"
        backText="Return to Pulse Surveys"
        onBack={() => navigate('/app/pulse-surveys')}
        controls={tabControls}
      />

      {effectiveActiveTab === 'schedule' ? (
        <>
          <SummarySection>
            <DashboardCard
              height="auto"
              padding="16px 18px"
            >
              <PulseSurveySummaryCardAny
                survey={survey}
                participation={participation}
                failedInvites={failedInvites}
                orgTimezone={orgTimezone}
                onToggleReminder={onToggleReminderEnabled}
                reminderToggleBusy={isTogglingReminderEnabled}
                actionsControl={surveyActionsControl}
              />
            </DashboardCard>
          </SummarySection>

          {finalized && (
            <PageSection padtop>
              <Message info data-testid="pulse-finalized-message">
                <Message.Header>
                  Finalized{participation.finalized_on ? ` on ${formatDate(participation.finalized_on)}` : ''}
                </Message.Header>
                <p>
                  This survey is closed. Totals above are final, invite links no longer work, and no additional
                  responses can be submitted.
                </p>
              </Message>
            </PageSection>
          )}

          {!finalized && (
            <PageSection padtop>
              <Table data-testid="pulse-participants-table">
                <TableHeader>
                  <TableRow>
                    <HeaderCell width={3}>Name</HeaderCell>
                    <HeaderCell width={4}>Email</HeaderCell>
                    <HeaderCell width={4}>Team</HeaderCell>
                    <HeaderCell width={2}>Status</HeaderCell>
                    <HeaderCell width={2}>Invitation sent</HeaderCell>
                    <HeaderCell width={1} justify="center">Actions</HeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedParticipants.map((p) => {
                    const canReinvite =
                      p.employee_id !== null && (p.invite_state === 'failed' || p.invite_state === 'canceled');
                    const canSendIndividualReminder = isInProgress && p.invite_state === 'sent';
                    const canOpenEmailHistory = Boolean(p.email_history && p.email_history.length > 0);
                    return (
                      <ParticipantTableRow
                        key={p.id}
                        $clickable={canOpenEmailHistory}
                        onClick={() => {
                          if (!canOpenEmailHistory) return;
                          onViewEmailHistory(p);
                        }}
                      >
                        <TableCell $firstcol width={3}>{p.name}</TableCell>
                        <TableCell width={4}>{p.email}</TableCell>
                        <TableCell width={4}>
                          <TeamNameText title={p.team_name || ''}>{p.team_name || 'N/A'}</TeamNameText>
                        </TableCell>
                        <TableCell width={2}>
                          <InviteStateLabel participant={p} />
                        </TableCell>
                        <TableCell width={2}>{p.invite_sent_at ? formatInviteSentDate(p.invite_sent_at) : 'N/A'}</TableCell>
                        <TableCell width={1} justify="center">
                          <ParticipantActionsControl onClick={(e) => e.stopPropagation()}>
                            <Dropdown
                              button
                              icon="ellipsis vertical"
                              floating
                              className="icon participant-actions-menu"
                              aria-label={`Actions for ${p.name}`}
                              direction="left"
                              disabled={busyId === p.id}
                            >
                              <Dropdown.Menu>
                                {isStaffOrSuperAdmin && (
                                  <Dropdown.Item
                                    icon="file alternate outline"
                                    text="Launch Member Survey"
                                    disabled={!isInProgress || !p.invitation_url}
                                    title={isInProgress ? undefined : 'Member survey can only be launched while this pulse survey is in progress.'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!p.invitation_url) return;
                                      window.open(p.invitation_url, '_blank', 'noopener,noreferrer');
                                    }}
                                  />
                                )}
                                {canReinvite && (
                                  <Dropdown.Item
                                    icon="redo"
                                    text={p.invite_state === 'failed' ? 'Retry Invite' : 'Re-invite'}
                                    disabled={isInviting}
                                    onClick={() => onReinvite(p)}
                                  />
                                )}
                                <Dropdown.Item
                                  icon="send"
                                  text="Send Reminder"
                                  disabled={!canSendIndividualReminder}
                                  title={canSendIndividualReminder ? undefined : 'Reminders can be sent only to currently invited participants while the survey is in progress.'}
                                  onClick={() => onRemindParticipant(p)}
                                />
                                <Dropdown.Item
                                  icon="history"
                                  text="View Email History"
                                  disabled={!p.email_history || p.email_history.length === 0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewEmailHistory(p);
                                  }}
                                />
                                <Dropdown.Item
                                  icon="cancel"
                                  text={p.invite_state === 'sent' ? 'Cancel Member Survey' : 'Remove'}
                                  disabled={p.invite_state === 'canceled'}
                                  onClick={() => onUninvite(p)}
                                />
                              </Dropdown.Menu>
                            </Dropdown>
                          </ParticipantActionsControl>
                        </TableCell>
                      </ParticipantTableRow>
                    );
                  })}
                  {filteredParticipants.length === 0 && (
                    <TableRow>
                      <TableCell colSpan="6" justify="center">
                        {participants.length === 0 ? 'No one has been invited yet.' : 'No participants match the current filters.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {participants.length > 0 && (
                <PageSection display="flex" justifyContent="start" data-testid="pulse-participants-pagination">
                  <ParticipantTableFooterControls>
                    <Dropdown
                      selection
                      compact
                      options={teamFilterOptions as any}
                      value={teamFilter}
                      onChange={(_, data) => setTeamFilter((data.value as string) || ALL_TEAMS_FILTER_VALUE)}
                      aria-label="Filter participants by team"
                    />
                    <Input
                      placeholder="Filter by member name"
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      aria-label="Filter participants by name"
                    />
                    {totalPages > 1 && (
                      <PaginationWrap>
                        <Pagination
                          activePage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(_, data) => setCurrentPage(data.activePage as number)}
                          firstItem={null}
                          lastItem={null}
                          prevItem={{ content: '‹', icon: true }}
                          nextItem={{ content: '›', icon: true }}
                        />
                      </PaginationWrap>
                    )}
                  </ParticipantTableFooterControls>
                </PageSection>
              )}
            </PageSection>
          )}
        </>
      ) : (
        <PulseSurveyLikertAnalytics
          surveyId={surveyId}
          validId={validId}
          organizationName={survey?.organization_name || organization?.name || null}
          selectedParticipantTeamId={selectedAnalyticsParticipantTeamId}
          onSelectedParticipantTeamIdChange={setSelectedAnalyticsParticipantTeamId}
          onParticipantTeamOptionsChange={setAnalyticsParticipantTeamOptions}
        />
      )}

      <PositionedModal open={confirmFinalize} onClose={() => setConfirmFinalize(false)} size="small">
        <Modal.Header>Finalize this pulse survey?</Modal.Header>
        <Modal.Content data-testid="pulse-finalize-confirm">
          <p>
            <strong>This cannot be undone.</strong> Finalizing will:
          </p>
          <ul>
            <li>Close this survey and lock participation so no additional responses can be submitted.</li>
            <li>Stop every survey link from working. No more responses can be submitted.</li>
            <li>Lock the survey so it can no longer be edited.</li>
          </ul>
          {finalizeError && <Message negative content={finalizeError} />}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmFinalize(false)} disabled={isFinalizing}>
            Cancel
          </Button>
          <Button negative loading={isFinalizing} disabled={isFinalizing} onClick={onFinalize}>
            Finalize permanently
          </Button>
        </Modal.Actions>
      </PositionedModal>

      <EmailHistoryModal
        open={historyModal.open}
        name={historyModal.name}
        email={historyModal.email}
        items={historyModal.items}
        formatDate={formatDate}
        onClose={() => setHistoryModal((prev) => ({ ...prev, open: false }))}
      />

      {survey && (
        <PulseSurveyFormModal
          open={isEditOpen}
          survey={survey}
          questionSets={questionSets as AssessmentQuestionSetOption[]}
          isSubmitting={isUpdatingSurvey}
          orgTimezone={orgTimezone}
          defaultReminderSchedule={organization?.defaultReminderSchedule ?? organization?.default_reminder_schedule ?? '0 9 */2 * *'}
          onClose={() => setIsEditOpen(false)}
          onSubmit={onSaveSurvey}
        />
      )}
    </PageContent>
  );
};

export default PulseSurveyDetails;

const SummarySection = styled.div`
  width: 100%;
  position: relative;
  z-index: 2;
`;

const ParticipantTableRow = styled(TableRow)<{ $clickable: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $clickable }) =>
    $clickable
      ? `
    &:hover {
      background-color: #f8fafc;
    }
  `
      : ''}
`;

const ParticipantActionsControl = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;

  .participant-actions-menu.ui.button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 4px;
    background: transparent;
    border: 1px solid transparent;
    box-shadow: none;
    padding: 0;
    line-height: 1;
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  }

  .participant-actions-menu.ui.button > i.icon {
    margin: 0;
    line-height: 1;
  }

  .participant-actions-menu.ui.button:hover,
  .participant-actions-menu.ui.button:focus-visible {
    background: #f8fafc;
    border-color: #98a2b3;
  }
`;

const SummaryActionsControl = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;

  .summary-actions-menu.ui.button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 4px;
    background: transparent;
    border: 1px solid transparent;
    box-shadow: none;
    padding: 0;
    line-height: 1;
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  }

  .summary-actions-menu.ui.button > i.icon {
    margin: 0;
    line-height: 1;
  }

  .summary-actions-menu.ui.button:hover,
  .summary-actions-menu.ui.button:focus-visible {
    background: #f8fafc;
    border-color: #98a2b3;
  }
`;

const TeamNameText = styled.span`
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;

const ParticipantTableFooterControls = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;

  .ui.selection.dropdown {
    min-width: 180px;
  }

  .ui.input {
    min-width: 220px;
  }
`;

const PaginationWrap = styled.div`
  margin-left: auto;
`;

const HeaderTeamFilterWrap = styled.div`
  margin-right: 12px;
  width: 280px;

  .ui.dropdown {
    width: 100%;
    min-width: 100%;
  }

  .ui.dropdown > .text {
    display: block;
    max-width: calc(100% - 28px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui.dropdown .menu {
    min-width: 100%;
    width: 100%;
  }

  .ui.dropdown .menu > .item {
    text-align: left;
  }
`;

const TabToggleWrap = styled.div`
  margin-right: 12px;
`;
